import { Component, Input, Output, EventEmitter, AfterViewInit, ViewChild, ViewEncapsulation, ElementRef, Renderer2, OnDestroy, AfterViewChecked, ChangeDetectionStrategy, inject } from '@angular/core';

import { InfiniteScrollService } from '../services';
import { HeaderSortService } from './header-sort/header-sort.service';
import { TableKeyBindService } from './keyboard-navigation/table-key-bind.service';

@Component({
    selector: 'app-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.scss'],
    providers: [InfiniteScrollService, HeaderSortService, TableKeyBindService],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TableComponent implements AfterViewInit, AfterViewChecked, OnDestroy {
    private readonly render = inject(Renderer2);
    private readonly tableKeyBindService = inject(TableKeyBindService);
    private readonly infiniteScrollService = inject(InfiniteScrollService);
    headerSortService = inject(HeaderSortService);

    @Input() public loadingItems: boolean;
    @Input() public loadingTable: boolean;
    @Input() public loadingTableText: string;
    @Input() public disableHover: boolean;
    @Input() public customClass: string;
    @Input() public rowSelection: boolean;
    @Input() public rowDeselection: boolean;
    @Input() public keyboardNavigation: boolean;

    @Output() public scrolled = new EventEmitter();

    @ViewChild('tbodyContainer') public bodyContainerElement: ElementRef;

    @ViewChild('tbody') private readonly bodyElement: ElementRef;
    @ViewChild('thead') private readonly headElement: ElementRef;
    private rowClickListenerFunc: any;

    ngAfterViewInit(): void {
        this.rowClickListener();
    }

    ngAfterViewChecked(): void {
        this.scrollbarVisible();
        this.dropEmptyRowVisible();
    }

    ngOnDestroy(): void {
        if (this.rowClickListenerFunc) {
            this.rowClickListenerFunc();
        }
    }

    public onScroll(): void {
        this.infiniteScrollService.takeNext();
        this.scrolled.emit();
    }

    public resetScrollPosition(): void {
        if (this.bodyElement) {
            this.bodyElement.nativeElement.scrollTop = 0;
        }
    }

    public resetActiveRow(): void {
        const rows = this.bodyElement.nativeElement.querySelectorAll('tr');
        if (!rows) {
            return;
        }

        rows.forEach((row: any) => {
            this.render.removeClass(row, 'active');
        });
    }

    public getLoadingScreenHeight(): number {
        if (!this.headElement || !this.bodyElement || !this.bodyContainerElement) {
            return 0;
        }

        const headHeight = this.headElement.nativeElement.offsetHeight;
        const bodyHeight = this.bodyElement.nativeElement.offsetHeight;
        const bodyContainerHeight = this.bodyContainerElement.nativeElement.offsetHeight;

        if (bodyContainerHeight < bodyHeight) {
            return headHeight + bodyContainerHeight;
        }

        return headHeight + bodyHeight;
    }

    private scrollbarVisible(): void {
        const bodyElement = this.bodyElement.nativeElement;
        const headElement = this.headElement.nativeElement;

        if (bodyElement && bodyElement.scrollHeight > bodyElement.clientHeight) {
            if (headElement.style.cssText.includes('padding-right')) {
                return;
            }
            setTimeout(() => {
                const scrollbarWidth = bodyElement.offsetWidth - bodyElement.clientWidth;
                this.render.setStyle(headElement, 'padding-right', `${scrollbarWidth}px`);
            });
        } else if (headElement.style.cssText.includes('padding-right')) {
            this.render.removeStyle(headElement, 'padding-right');
        }
    }

    private dropEmptyRowVisible(): void {
        const dropList = this.bodyContainerElement.nativeElement.querySelector('.cdk-drop-list:not(tr)');
        if (!dropList) {
            return;
        }

        const row = dropList.querySelector('tr');
        if (row == null && !dropList.classList.contains('drop-empty-row')) {
            this.render.addClass(dropList, 'drop-empty-row');
        } else if (
            dropList.classList.contains('drop-empty-row') &&
            row != null &&
            (!row.classList.contains('cdk-drag-dragging') || row.classList.contains('cdk-drag-placeholder'))
        ) {
            this.render.removeClass(dropList, 'drop-empty-row');
        }
    }

    private activeRow(element: Element): void {
        if (!this.rowSelection) {
            return;
        }

        if (this.rowDeselection) {
            const active = element.classList.contains('active');
            if (active) {
                this.render.removeClass(element, 'active');
                return;
            }
        }

        this.resetActiveRow();
        this.render.addClass(element, 'active');
    }

    private rowClickListener(): void {
        if (!this.rowSelection) {
            return;
        }

        const containerElement = this.bodyContainerElement.nativeElement;
        if (containerElement) {
            this.rowClickListenerFunc = this.render.listen(containerElement, 'click', (event: any) => {
                const rowElement = event.target.closest('tr');
                if (rowElement) {
                    this.tableKeyBindService.setCurrentElement(rowElement);
                    this.activeRow(rowElement);
                }
            });
        }
    }
}
